import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import queryString from "query-string-for-all";
import { RouteComponentProps } from "react-router";
import Helmet from "react-helmet";
import { Wrap } from "../../../components/Layout/Page";
import { ComfortZones } from "../../../components/Reports/Comfort/ComfortZones";
import { StyledBackgroundRow } from "../../../components/Reports/Reports.styles";
import { HeadBar } from "../../../components/Reports/Comfort/components/HeadBar";
import { getSiteById } from "../../../utils/state";
import { COMFORT_PERSIST_KEY } from "../../../context/reports/comfort/constants";
import { useActions as useComfortActions } from "../../../context/reports/comfort/useActions";
import { useState as useComfortState } from "../../../context/reports/comfort";
import { useZoneEffects } from "./hooks/useZoneEffects";
import {
  updateIsMultiSiteRoute,
  selectActiveSite,
} from "../../../redux/modules/_BETA/selections";

interface ConnectedProps {
  appReady: boolean;
}

interface DispatchProps {
  setMultiSite(syncoId: string): void;
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    setMultiSite: (syncoId: string) => {
      dispatch(selectActiveSite(syncoId));
      dispatch(updateIsMultiSiteRoute(false));
    },
  };
}

interface MatchParams {
  siteId: string;
}

type Props = ConnectedProps & DispatchProps & RouteComponentProps<MatchParams>;

const mapStateToProps = (state: Insite.AppState): ConnectedProps => ({
  appReady: state.appSetup.appSetupComplete,
});

const Scene = React.memo<Props>(
  ({ appReady, match, history, setMultiSite }) => {
    const { siteId } = match.params;
    const state = useComfortState();
    const sites = React.useMemo(() => {
      const qs = queryString.parse(history.location.search, {
        arrayFormat: "comma",
      });
      return Array.isArray(qs.sites) ? qs.sites : ([qs.sites] as string[]);
    }, [history.location.search]);

    React.useEffect(() => {
      // * Update the global site select to display current site name
      setMultiSite(siteId);
    }, []);

    useZoneEffects({ appReady, sites, siteId });

    const { setTimeFrame } = useComfortActions();

    if (!appReady) {
      return null;
    }

    return (
      <React.Fragment>
        <Helmet title="Comfort" />
        <StyledBackgroundRow>
          <HeadBar
            backTo={
              sites.length > 1 ? "/reports/performance/comfort" : undefined
            }
            groupName={getSiteById(siteId).label}
            defaultTimeFrame={state.timeFrameOption}
            defaultCompareTo={state.compareToOption}
            onTimeFrameChange={setTimeFrame}
            persistKey={COMFORT_PERSIST_KEY}
          />
        </StyledBackgroundRow>
        <Wrap>
          <ComfortZones siteId={siteId} />
        </Wrap>
      </React.Fragment>
    );
  },
  (p, n) => p.match.params === n.match.params
);

export const ZonesScene = connect(mapStateToProps, mapDispatchToProps)(Scene);
